import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import LanguageToggle from "@/components/language-toggle";
import { ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Apply() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", country: "United Kingdom", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", form);
      setDone(true);
      toast.success(t.apply.success);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#f6f2ea] flex flex-col">
      <header className="max-w-7xl mx-auto w-full px-6 md:px-10 py-6 flex justify-between items-center">
        <Link to="/" data-testid="apply-back-btn" className="inline-flex items-center gap-2 text-[#0d3b66] text-sm hover:opacity-70">
          <ArrowLeft size={16} /> {t.common.back}
        </Link>
        <LanguageToggle />
      </header>

      <main className="flex-1 grid md:grid-cols-2 gap-0">
        <div className="hidden md:block relative">
          <img src="https://images.unsplash.com/photo-1559026432-016825fa59df?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0d3b66]/25" />
          <div className="absolute bottom-10 left-10 right-10 text-white">
            <div className="text-xs uppercase tracking-[0.2em] text-[#f4a261]">Smile Ambassador</div>
            <h2 className="font-serif-display text-5xl mt-4 leading-tight">Share a smile.<br/>Earn rewards.</h2>
          </div>
        </div>

        <div className="flex items-center justify-center px-6 md:px-16 py-14">
          <div className="w-full max-w-md fade-up">
            {done ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#0d3b66] flex items-center justify-center">
                  <PaperPlaneTilt size={28} weight="duotone" color="#f4a261" />
                </div>
                <h1 className="font-serif-display text-3xl mt-6">{t.apply.success}</h1>
                <p className="text-[#3b4b5a] mt-3">Please check your inbox for further steps.</p>
                <Link data-testid="apply-home-btn" to="/" className="inline-block mt-8 px-6 py-3 rounded-full bg-[#0d3b66] text-[#f4a261] text-sm">← Home</Link>
              </div>
            ) : (
              <>
                <h1 className="font-serif-display text-4xl">{t.apply.title}</h1>
                <p className="text-[#3b4b5a] mt-3 text-sm">{t.apply.sub}</p>
                <form onSubmit={submit} className="mt-8 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="apply-first-name" required placeholder={t.apply.firstName} value={form.name} onChange={set("name")} className="input" />
                    <input data-testid="apply-surname" required placeholder={t.apply.surname} value={form.surname} onChange={set("surname")} className="input" />
                  </div>
                  <input data-testid="apply-email" required type="email" placeholder={t.apply.email} value={form.email} onChange={set("email")} className="input" />
                  <input data-testid="apply-phone" required placeholder={t.apply.phone} value={form.phone} onChange={set("phone")} className="input" />
                  <input data-testid="apply-country" placeholder={t.apply.country} value={form.country} onChange={set("country")} className="input" />
                  <textarea data-testid="apply-message" placeholder={t.apply.message} rows={4} value={form.message} onChange={set("message")} className="input resize-none" />
                  <button data-testid="apply-submit-btn" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#0d3b66] text-[#f4a261] font-medium disabled:opacity-60 hover:bg-[#071f36]">
                    {loading ? t.common.loading : t.apply.submit}
                  </button>
                </form>
                <p className="text-xs text-[#6b7280] mt-4">Already applied? <Link data-testid="apply-goto-login" to="/login" className="underline">{t.nav.login}</Link></p>
              </>
            )}
          </div>
        </div>
      </main>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 12px;
          background: white;
          border: 1px solid #e6dfd2;
          font-size: 14px;
          outline: none;
        }
        .input:focus { border-color: #0d3b66; box-shadow: 0 0 0 3px rgba(13,59,102,0.08); }
      `}</style>
    </div>
  );
}
